import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ChevronRightIcon, CodeIcon, UsersIcon, BriefcaseIcon, StarIcon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, AwardIcon, SearchIcon, ClockIcon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { testimonials, developers } from '../utils/data';
import { TechnologyLinksSection } from '../components/sections/TechnologyLinksSection';
import { TalentCapsuleCard } from '../components/developers/TalentCapsuleCard';
const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState({
    stats: false
  });
  const statsRef = useRef<HTMLDivElement>(null);
  const slideTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-advance slides
  useEffect(() => {
    const startTimer = () => {
      slideTimerRef.current = setTimeout(() => {
        handleSlideChange((activeSlide + 1) % 2);
      }, 6000); // 6 seconds per slide
    };
    startTimer();
    return () => {
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
      }
    };
  }, [activeSlide]);
  const handleSlideChange = (index: number) => {
    if (index === activeSlide || isTransitioning) return;
    setIsTransitioning(true);
    setActiveSlide(index);
    // Reset transitioning state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800);
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target === statsRef.current) {
            setIsVisible(prev => ({
              ...prev,
              stats: true
            }));
          }
        }
      });
    }, {
      threshold: 0.2
    });
    if (statsRef.current) observer.observe(statsRef.current);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);
  // Stats
  const stats = [{
    number: '4,500+',
    label: 'Certified Developers',
    icon: AwardIcon
  }, {
    number: '50+',
    label: 'Hiring Companies',
    icon: BriefcaseIcon
  }, {
    number: '100+',
    label: 'Job Openings',
    icon: TrendingUpIcon
  }, {
    number: '15+',
    label: 'Certifications',
    icon: ShieldCheckIcon
  }];
  return <div className="w-full overflow-hidden bg-[#0F1115] relative" data-id="element-977">
      {/* Global Grain Texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-978"></div>

      {/* Hero Slideshow Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-id="element-979">
        {/* Slide 1 - Developers */}
        <div className={`absolute inset-0 transition-opacity duration-800 ${activeSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-980">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-981">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-982">
              <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#d73b3e]/10 to-transparent rounded-full blur-3xl" style={{
              transform: `translateY(${scrollY * 0.3}px)`
            }} data-id="element-983" />
              <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full blur-3xl" style={{
              transform: `translateY(${-scrollY * 0.2}px)`
            }} data-id="element-984" />
              {/* Grid pattern overlay */}
              <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                   linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }} data-id="element-985" />
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 h-screen flex items-center" data-id="element-986">
            <div className="grid lg:grid-cols-2 gap-16 items-center w-full text-white" data-id="element-987">
              {/* Left content */}
              <div className={`transition-all duration-1000 ${activeSlide === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
              transitionDelay: activeSlide === 0 ? '200ms' : '0ms'
            }} data-id="element-988">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6" data-id="element-989">
                  <TrendingUpIcon className="w-4 h-4 text-[#d73b3e]" data-id="element-990" />
                  <span className="text-sm font-medium" data-id="element-991">
                    Join 4,500+ certified developers
                  </span>
                </div>

                <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-992">
                  Showcase your{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-993">
                    certified
                  </span>{' '}
                  and verifiable tech skills
                </h1>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-994">
                  Build your professional profile, display your certifications,
                  and connect with companies actively hiring. Stand out with
                  verified credentials.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-995">
                  <Link to="/signup" className="group px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center" data-id="element-996">
                    Register
                    <ChevronRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-997" />
                  </Link>
                  <Link to="/jobs" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20 flex items-center justify-center" data-id="element-998">
                    Browse Jobs
                  </Link>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-6" data-id="element-999">
                  <div data-id="element-1000">
                    <div className="text-3xl font-bold mb-1" data-id="element-1001">30%</div>
                    <div className="text-sm text-gray-400" data-id="element-1002">Higher rates</div>
                  </div>
                  <div data-id="element-1003">
                    <div className="text-3xl font-bold mb-1" data-id="element-1004">7 days</div>
                    <div className="text-sm text-gray-400" data-id="element-1005">Avg. to hired</div>
                  </div>
                  <div data-id="element-1006">
                    <div className="text-3xl font-bold mb-1" data-id="element-1007">100+</div>
                    <div className="text-sm text-gray-400" data-id="element-1008">Active jobs</div>
                  </div>
                </div>
              </div>

              {/* Right visual - Video showcase */}
              <div className={`relative transition-all duration-1000 ${activeSlide === 0 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{
              transitionDelay: activeSlide === 0 ? '400ms' : '0ms'
            }} data-id="element-1009">
                <div className="relative w-full max-w-lg mx-auto" data-id="element-1010">
                  {/* Video Container */}
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20 transform rotate-2 hover:rotate-0 transition-transform duration-500 bg-gray-900" data-id="element-1011">
                    <div className="aspect-video w-full" data-id="element-1012">
                      <video src="https://cdn.magicpatterns.com/uploads/hsKHrSfTuhHznpp9VrtCWc/Schermopname_2025-12-21_163931.undefined" autoPlay loop muted playsInline className="w-full h-full object-contain" data-id="element-1013" />
                    </div>

                    {/* Overlay gradient for polish */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 to-transparent pointer-events-none" data-id="element-1014" />
                  </div>

                  {/* Decorative elements behind */}
                  <div className="absolute -z-10 top-10 -right-10 w-full h-full bg-gradient-to-br from-[#d73b3e]/20 to-orange-500/20 rounded-2xl blur-xl transform rotate-6" data-id="element-1015" />

                  {/* Floating badge */}
                  <div className="absolute -bottom-6 -left-6 bg-[#1a1d24] p-4 rounded-xl shadow-xl flex items-center gap-3 animate-bounce border border-gray-800" style={{
                  animationDuration: '3s'
                }} data-id="element-1016">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center" data-id="element-1017">
                      <CheckCircle2Icon className="w-6 h-6 text-green-500" data-id="element-1018" />
                    </div>
                    <div data-id="element-1019">
                      <div className="text-sm font-bold text-white" data-id="element-1020">
                        Verified
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-1021">
                        Certificate Valid
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide 2 - Recruiters */}
        <div className={`absolute inset-0 transition-opacity duration-800 ${activeSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} data-id="element-1022">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-1023">
            {/* Animated grid background */}
            <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-1024">
              <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                   linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              transform: `translateY(${scrollY * 0.1}px)`
            }} data-id="element-1025" />
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 h-screen flex items-center" data-id="element-1026">
            <div className="grid lg:grid-cols-2 gap-16 items-center w-full text-white" data-id="element-1027">
              {/* Left visual - Technology logos showcase */}
              <div className={`relative transition-all duration-1000 ${activeSlide === 1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{
              transitionDelay: activeSlide === 1 ? '200ms' : '0ms'
            }} data-id="element-1028">
                <div className="relative" data-id="element-1029">
                  {/* Technology logos grid */}
                  <div className="grid grid-cols-3 gap-4 max-w-md" data-id="element-1030">
                    {/* Row 1 */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.1s both' : 'none'
                  }} data-id="element-1031">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/9a/Laravel.svg" alt="Laravel" className="w-12 h-12 object-contain" data-id="element-1032" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.2s both' : 'none'
                  }} data-id="element-1033">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg" alt="React" className="w-12 h-12 object-contain" data-id="element-1034" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.3s both' : 'none'
                  }} data-id="element-1035">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/95/Vue.js_Logo_2.svg" alt="Vue.js" className="w-12 h-12 object-contain" data-id="element-1036" />
                    </div>

                    {/* Row 2 */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.4s both' : 'none'
                  }} data-id="element-1037">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/2/27/PHP-logo.svg" alt="PHP" className="w-16 h-10 object-contain" data-id="element-1038" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.5s both' : 'none'
                  }} data-id="element-1039">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/4/4c/Typescript_logo_2020.svg" alt="TypeScript" className="w-12 h-12 object-contain" data-id="element-1040" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.6s both' : 'none'
                  }} data-id="element-1041">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/d/d9/Node.js_logo.svg" alt="Node.js" className="w-12 h-12 object-contain" data-id="element-1042" />
                    </div>

                    {/* Row 3 */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.7s both' : 'none'
                  }} data-id="element-1043">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/d/d5/Tailwind_CSS_Logo.svg" alt="Tailwind CSS" className="w-12 h-12 object-contain" data-id="element-1044" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.8s both' : 'none'
                  }} data-id="element-1045">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/3/3f/Git_icon.svg" alt="Git" className="w-12 h-12 object-contain" data-id="element-1046" />
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex items-center justify-center hover:bg-white/15 transition-all duration-300" style={{
                    animation: activeSlide === 1 ? 'slideInLeft 0.6s ease-out 0.9s both' : 'none'
                  }} data-id="element-1047">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" alt="AWS" className="w-12 h-12 object-contain" data-id="element-1048" />
                    </div>
                  </div>

                  {/* Floating stats badge */}
                  <div className="absolute -top-8 -right-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 shadow-2xl" data-id="element-1049">
                    <div className="text-2xl font-bold text-white" data-id="element-1050">92%</div>
                    <div className="text-xs opacity-90 text-white" data-id="element-1051">
                      Match rate
                    </div>
                  </div>

                  {/* Additional floating badge */}
                  <div className="absolute -bottom-6 left-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-3 shadow-2xl" data-id="element-1052">
                    <div className="text-lg font-bold text-white" data-id="element-1053">4,500+</div>
                    <div className="text-xs opacity-90 text-white" data-id="element-1054">
                      Developers
                    </div>
                  </div>
                </div>
              </div>

              {/* Right content */}
              <div className={`transition-all duration-1000 ${activeSlide === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
              transitionDelay: activeSlide === 1 ? '400ms' : '0ms'
            }} data-id="element-1055">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6" data-id="element-1056">
                  <ZapIcon className="w-4 h-4 text-[#d73b3e]" data-id="element-1057" />
                  <span className="text-sm font-medium" data-id="element-1058">
                    Trusted by 500+ companies
                  </span>
                </div>

                <h2 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-1059">
                  Recruit the best{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1060">
                    tech talent
                  </span>{' '}
                  with verified certifications
                </h2>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed" data-id="element-1061">
                  Access 4,500+ certified developers with proven skills. Filter
                  by expertise, availability, and rate. Hire with confidence.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-1062">
                  <Link to="/developers" className="group px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center" data-id="element-1063">
                    Browse Certified Devs
                    <SearchIcon className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" data-id="element-1064" />
                  </Link>
                  <Link to="/post-job" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20 flex items-center justify-center" data-id="element-1065">
                    Post a job
                  </Link>
                </div>

                {/* Technology expertise callout */}
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10" data-id="element-1066">
                  <h3 className="text-sm font-semibold text-gray-300 mb-3" data-id="element-1067">
                    Find experts in:
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1068">
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-1069">
                      Laravel
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-1070">
                      React
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-1071">
                      Vue.js
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-1072">
                      TypeScript
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-1073">
                      Node.js
                    </span>
                    <span className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-full font-medium" data-id="element-1074">
                      +20 more
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Navigation Dots */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex gap-3" data-id="element-1075">
          {[0, 1].map(index => <button key={index} onClick={() => handleSlideChange(index)} className={`transition-all duration-300 rounded-full ${activeSlide === index ? 'w-12 h-3 bg-[#d73b3e]' : 'w-3 h-3 bg-white/40 hover:bg-white/60'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-1076" />)}
        </div>
      </section>

      {/* Technology Links Section */}
      <section className="bg-[#1a1d24] py-16 border-t border-gray-800" data-id="element-1077">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1078">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 leading-tight" data-id="element-1079">
            Browse by{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1080">
              Technology
            </span>
          </h2>
          <TechnologyLinksSection data-id="element-1081" />
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-[#0F1115]" data-id="element-1082">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1083">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1084">
            {stats.map((stat, index) => {
            const Icon = stat.icon;
            return <div key={index} className={`text-center transition-all duration-700 ${isVisible.stats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
              transitionDelay: `${index * 100}ms`
            }} data-id="element-1085">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl mb-4 border border-white/5" data-id="element-1086">
                    <Icon className="w-8 h-8 text-[#d73b3e]" data-id="element-1087" />
                  </div>
                  <div className="text-4xl font-bold text-white mb-2" data-id="element-1088">
                    {stat.number}
                  </div>
                  <div className="text-sm font-medium text-gray-400" data-id="element-1089">
                    {stat.label}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Developers Section */}
      <section className="py-24 bg-gradient-to-br from-[#1a1d24] to-[#0F1115] relative overflow-hidden" data-id="element-1090">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5" data-id="element-1091">
          <div className="absolute top-20 right-0 w-96 h-96 bg-[#d73b3e] rounded-full blur-3xl" data-id="element-1092"></div>
          <div className="absolute bottom-20 left-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl" data-id="element-1093"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1094">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1095">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#d73b3e]/10 backdrop-blur-sm rounded-full border border-[#d73b3e]/20 mb-6" data-id="element-1096">
              <StarIcon className="w-4 h-4 text-[#d73b3e]" data-id="element-1097" />
              <span className="text-sm font-medium text-[#d73b3e]" data-id="element-1098">
                Top Talent
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-1099">
              Meet our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1100">
                certified developers
              </span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1101">
              Handpicked professionals with verified skills and proven track
              records. Ready to join your team today.
            </p>
          </div>

          {/* Featured Developer Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-1102">
            {developers.slice(0, 6).map((developer, index) => <div key={developer.id} className="opacity-0 animate-fade-in" style={{
            animation: `fadeInUp 0.6s ease-out ${index * 0.1}s forwards`
          }} data-id="element-1103">
                <TalentCapsuleCard developer={developer} data-id="element-1104" />
              </div>)}
          </div>

          {/* CTA */}
          <div className="text-center" data-id="element-1105">
            <Link to="/developers" className="inline-flex items-center gap-3 px-8 py-4 bg-white/5 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/10 transition-all duration-300 border-2 border-white/10 hover:border-[#d73b3e] group" data-id="element-1106">
              View all {developers.length} certified developers
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1107" />
            </Link>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1d24] to-[#0F1115]" data-id="element-1108">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1109">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1110">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight" data-id="element-1111">
              Why choose{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1112">
                CertifiedDevs?
              </span>
            </h2>
            <p className="text-lg text-gray-400" data-id="element-1113">
              The trusted platform for Laravel talent
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-1114">
            <div className="bg-[#1a1d24] p-8 rounded-2xl shadow-lg border border-gray-800 hover:border-gray-700 transition-colors" data-id="element-1115">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6 border border-white/5" data-id="element-1116">
                <CodeIcon className="h-7 w-7 text-[#d73b3e]" data-id="element-1117" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white" data-id="element-1118">
                Vetted Developers
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1119">
                All developers are manually reviewed and verified to ensure
                quality.
              </p>
            </div>
            <div className="bg-[#1a1d24] p-8 rounded-2xl shadow-lg border border-gray-800 hover:border-gray-700 transition-colors" data-id="element-1120">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6 border border-white/5" data-id="element-1121">
                <ShieldCheckIcon className="h-7 w-7 text-[#d73b3e]" data-id="element-1122" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white" data-id="element-1123">
                Certified Expertise
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1124">
                Industry-recognized certifications that validate real skills and
                knowledge.
              </p>
            </div>
            <div className="bg-[#1a1d24] p-8 rounded-2xl shadow-lg border border-gray-800 hover:border-gray-700 transition-colors" data-id="element-1125">
              <div className="w-14 h-14 bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl flex items-center justify-center mb-6 border border-white/5" data-id="element-1126">
                <ZapIcon className="h-7 w-7 text-[#d73b3e]" data-id="element-1127" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white" data-id="element-1128">
                Fast Matching
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1129">
                Advanced filters and search to find the perfect match in days,
                not weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection testimonials={testimonials} data-id="element-1130" />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white border-t border-gray-800" data-id="element-1131">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1132">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1133">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-1134">
              Ready to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d73b3e] to-orange-500" data-id="element-1135">
                get started?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-10" data-id="element-1136">
              Join thousands of developers and companies already using
              CertifiedDevs
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1137">
              <Link to="/signup" className="px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1138">
                Get Certified
              </Link>
              <Link to="/developers" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white/20" data-id="element-1139">
                Browse Developers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Home;
// Helper functions at the bottom of the file
const getFlagEmoji = (country: string) => {
  const countryFlags: {
    [key: string]: string;
  } = {
    'United States': '🇺🇸',
    Uganda: '🇺🇬',
    Germany: '🇩🇪',
    Philippines: '🇵🇭',
    Egypt: '🇪🇬',
    Italy: '🇮🇹',
    Romania: '🇷🇴',
    France: '🇫🇷',
    Spain: '🇪🇸',
    Nigeria: '🇳🇬',
    Pakistan: '🇵🇰',
    'United Kingdom': '🇬🇧',
    Switzerland: '🇨🇭',
    Indonesia: '🇮🇩',
    Turkey: '🇹🇷'
  };
  return <span className="text-lg" data-id="element-1140">{countryFlags[country] || '🏳️'}</span>;
};
const getSkillIcon = (skill: string) => {
  const skillIcons: {
    [key: string]: string;
  } = {
    Laravel: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6raD3sQbYhrAYJrpu7Fmsg/Laravel.jpg',
    Python: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rmryh1szxRZK8Ckh29GLMA/python.png',
    React: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ptv5Wif4YhmkFBsFHL24ST/React.png',
    Symfony: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7M5FR1J9g2nFRdnfNT5c1d/Symfony.png',
    'Vue.js': 'https://uploadthingy.s3.us-west-1.amazonaws.com/ncaFrZrfpfapyV9ExA35DY/Vue_logo.png',
    // Fallback for other skills
    PHP: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/27/PHP-logo.svg/1280px-PHP-logo.svg.png',
    JavaScript: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/JavaScript-logo.png/800px-JavaScript-logo.png',
    TypeScript: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Typescript_logo_2020.svg/1200px-Typescript_logo_2020.svg.png'
  };
  return skillIcons[skill] || 'https://via.placeholder.com/30?text=' + skill.charAt(0);
};
const getSkillColor = (skill: string) => {
  const skillColors: {
    [key: string]: string;
  } = {
    Laravel: '#FF3B30',
    PHP: '#8892BF',
    'Vue.js': '#42B883',
    React: '#61DAFB',
    JavaScript: '#F7DF1E',
    TypeScript: '#3178C6',
    MySQL: '#4479A1',
    'API Development': '#6DB33F',
    'Tailwind CSS': '#38B2AC',
    'Alpine.js': '#77C1D2',
    Livewire: '#FB70A9',
    Docker: '#2496ED',
    CSS: '#264DE4',
    HTML: '#E34F26'
  };
  return skillColors[skill] || '#CBD5E0';
};
<style jsx data-id="element-1141">{`
  @keyframes spin-slow {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin-slow {
    animation: spin-slow 20s linear infinite;
  }
  @keyframes ping-slow {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.5);
      opacity: 0.4;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }
  .animate-ping-slow {
    animation: ping-slow 3s ease-in-out infinite;
  }
  .animation-delay-500 {
    animation-delay: 0.5s;
  }
  .animation-delay-1000 {
    animation-delay: 1s;
  }
  .animation-delay-1500 {
    animation-delay: 1.5s;
  }
  svg line {
    stroke-dasharray: 5, 5;
    animation: dash 30s linear infinite;
  }
  @keyframes dash {
    to {
      stroke-dashoffset: 1000;
    }
  }
`}</style>;